/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useEffect, useRef, useState } from 'react';
import { Modal, Input, message } from 'antd';
import { useIntl } from 'react-intl';
import { ModalProps } from 'antd/lib/modal';
import { useMutation, queryCache } from 'react-query';

import { ThemedStyleFn } from '~src/hooks/useStyles';
import { DbFolder } from '~src/types/db';
import { DB_STATE } from '~src/types/enums';
import { createTag, editTag } from '~src/services/api';
import { appLog } from '~src/utils/log';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { useProjectId } from '~src/hooks/useProjectId';
import { Tag } from '~src/services/api/archiveSettings';
import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';

export interface CreateEditTagModalProps extends Partial<ModalProps> {
  parent?: DbFolder;
  tag?: Tag | null;
}
export const CreateEditTagModal: React.FC<CreateEditTagModalProps> = ({
  parent,
  onCancel,
  tag,
  ...modalProps
}) => {
  const intl = useIntl();
  const projectId = useProjectId();
  const [nameVal, setNameVal] = useState(tag?.title || '');
  const nameInputRef = useRef<Input>(null);
  const isEditModal = Boolean(tag?.id);

  const archiveSettings = useArchiveSettings();
  const tagQuery = archiveSettings.data?.resTags;

  const [doCreateTag, { isLoading }] = useMutation(createTag, {
    onSuccess: (theFolder) => {
      // hide modal
      // @ts-ignore-line
      onCancel && onCancel();

      queryCache.invalidateQueries('archiveSettingsList');

      // reset values
      setNameVal('');
    },
    onError: (error, item) => {
      message.error(intl.formatMessage({ id: 'folder-create-tag-failed' }));
      appLog('CreateEditTagModal -> createTag -> item', item);
      appLog('CreateEditTagModal -> createTag -> err', error);
    },
  });

  const [doEditTag, { isLoading: isLoadingEdit }] = useMutation(editTag, {
    onSuccess: (theFolder) => {
      // hide modal
      // @ts-ignore-line
      onCancel && onCancel();
      queryCache.invalidateQueries('archiveSettingsList');

      // reset values
      setNameVal('');
    },
    onError: (error, item) => {
      message.error(intl.formatMessage({ id: 'folder-edit-tag-failed' }));
      appLog('EditEditTagModal -> createTag -> item', item);
      appLog('EditEditTagModal -> createTag -> err', error);
    },
  });

  useEffect(() => {
    if (modalProps.visible) {
      setTimeout(() => nameInputRef.current?.focus(), 350);

      // fetch folders if data is older than 1min
      if (Date.now() - archiveSettings.updatedAt > 60 * 1000) {
        archiveSettings.refetch();
      }
    }
  }, [modalProps.visible]);

  const isValid = () => {
    if (!nameVal) return false;
    // check if tag with the same name exists
    if (tagQuery?.length) {
      const trimmedName = nameVal.trim();
      const doesExist = tagQuery.some(
        (tag) => tag.title.trim() === trimmedName
      );
      if (doesExist) return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!isValid()) return;
    if (tag?.id) {
      doEditTag({
        projectId: String(projectId),
        id: String(tag.id),
        tagData: {
          title: nameVal,
        },
      });
    } else {
      doCreateTag({
        projectId: String(projectId),
        tagData: {
          title: nameVal,
        },
      });
    }
  };

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({
            id: tag?.title ? 'edit-tag' : 'create-tag',
          })}
          icon={['far', isEditModal ? 'pen' : 'circle-plus']}
        />
      }
      okText={intl.formatMessage({ id: 'save' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      width={420}
      centered
      okButtonProps={{ disabled: !isValid(), loading: isLoading }}
      onOk={handleSubmit}
      onCancel={onCancel}
      {...modalProps}>
      <Input
        ref={nameInputRef}
        placeholder={intl.formatMessage({ id: 'name' })}
        allowClear
        autoCapitalize="words"
        value={nameVal}
        maxLength={20}
        onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
        onChange={({ target: { value } }) =>
          setNameVal(value.charAt(0).toUpperCase() + value.slice(1))
        }
      />
      {/*    <Input.TextArea
        css={{
          marginTop: 10,
        }}
        placeholder={intl.formatMessage({ id: 'description' })}
        value={descriptionVal}
        onChange={({ target: { value } }) => setDescriptionVal(value)}
      /> */}
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({});
